import styled from 'styled-components';
import { ClearButton } from '#/shared/components/Styled';
import {
  XSPHONE_BREAK,
  PHONE_BREAK,
  TABLET_BREAK,
  DESKTOP_MIN_BREAK,
  XLDESKTOP_BREAK,
  GTM_DATA_ATTRIBUTE,
} from '#/shared/constants';
import GTM_HANDLES from '#/shared/gtm-handles';

const BaseButton = styled(ClearButton).attrs({ role: 'link' })`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 8px 10px 10px 10px;
  width: 33%;
  max-width: 33%;
  flex: 1 1 auto;
  text-align: center;
  cursor: pointer;
  margin: 5px;
  border-radius: ${props => props.theme.buttonRadiusLarge};
`;

export const TrackButton = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 100%;
  padding: 0.25em 0;
  background-color: ${props =>
    props.dropOffOnly ? props.theme.dangerColor : props.theme.buttonColor1};
  color: ${props => props.theme.buttonText1};
  border-radius: 3px;
  justify-content: center;
  font-size: 1em;
  @media (min-width: ${XSPHONE_BREAK}) {
    font-size: 1.1em;
  }
  @media (min-width: ${PHONE_BREAK}) {
    font-size: 1.15em;
  }
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    font-size: 1.2em;
    padding: 0.125em 0;
    border-radius: ${props => props.theme.buttonRadiusSmall};
  }
  &:focus {
    background-color: ${props => props.theme.buttonColor1Hover};
  }
  @media (any-hover: hover) {
    &:hover {
      background-color: ${props => props.theme.dangerColorHover};
    }
  }
`;

export const SignExpansionPanel = styled.div`
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: flex-end;
  background-color: ${props => props.theme.secondaryBackgroundColor};
`;

export const ArrivalsList = styled.div`
  display: flex;
  flex: 1 1 auto;
  padding: 8px;
  max-height: 190px;
`;

export const Arrival = styled(BaseButton).attrs({
  [GTM_DATA_ATTRIBUTE]: GTM_HANDLES.stop.arrivalButton,
})`
  border: solid 1px
    ${props => (props.isEstimated ? props.theme.primaryFontColor : 'none')};
  background-color: ${props =>
    props.isEstimated ? 'transparent' : props.theme.secondaryBackgroundColor};
  color: ${props =>
    props.isEstimated ? props.theme.primaryFontColor : props.theme.buttonText2};

  @media (any-hover: hover) {
    &:hover {
      background-color: ${props =>
        props.isEstimated
          ? 'transparent'
          : props.theme.secondaryBackgroundColor};
    }
    &:hover ${TrackButton} {
      background-color: ${props =>
        props.dropOffOnly
          ? props.theme.dangerColorHover
          : props.theme.buttonColor1Hover};
    }
  }

  &:disabled,
  &:disabled:hover {
    cursor: default;
    background-color: ${props =>
      !props.isEstimated ? props.theme.buttonColor2 : 'transparent'};
  }
`;

export const ButtonDetailWrap = styled.div`
  display: flex;
  width: 100%;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
`;

export const ButtonDetail = styled.div`
  display: flex;
  width: 100%;
  flex: 1 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  font-size: 0.8em;
  @media (min-width: ${XSPHONE_BREAK}) {
    font-size: 0.9em;
  }
  @media (min-width: ${PHONE_BREAK}) {
    font-size: 1em;
  }
  @media (min-width: ${TABLET_BREAK}) {
    font-size: 1.2em;
  }
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    font-size: 0.9em;
  }
  @media (min-width: ${XLDESKTOP_BREAK}) {
    font-size: 1em;
  }
`;

export const ButtonEmphasis = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: flex-start;
  line-height: 1;
  margin: 0.1em 0 0.25em 0;
  font-weight: 600;
`;

const arrivalTextStyles = styled.div`
  line-height: 0.9;
  margin: 0 0 0.1em;
  font-size: 1.8em;
  @media (min-width: ${XSPHONE_BREAK}) {
    font-size: 1.9em;
  }
  @media (min-width: ${PHONE_BREAK}) {
    font-size: 1.9em;
  }
  @media (min-width: ${TABLET_BREAK}) {
    font-size: 2.2em;
  }
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    font-size: 1.9em;
  }
`;

export const DueText = styled(arrivalTextStyles)`
  line-height: 1;
`;
export const EstimatedTimeText = styled(arrivalTextStyles)``;
export const ScheduledArrivalText = styled(arrivalTextStyles)``;

export const MilesAwayText = styled(arrivalTextStyles)`
  font-size: 1.1em;
  @media (min-width: ${XSPHONE_BREAK}) {
    font-size: 1.2em;
  }
  @media (min-width: ${PHONE_BREAK}) {
    font-size: 1.3em;
  }
  @media (min-width: ${TABLET_BREAK}) {
    font-size: 1.5em;
  }
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    font-size: 1.1em;
  }
`;

export const CanceledTimeText = styled(arrivalTextStyles)`
  color: ${props => props.theme.dangerColor};
  font-weight: normal;
`;

export const CanceledLabel = styled(ButtonDetail)`
  justify-content: flex-end;
  color: ${props => props.theme.dangerColor};
`;

export const FullScheduleAlerts = styled.div`
  display: flex;
  flex: 1 0 auto;
  padding: 0 8px 8px 8px;
`;

const FullScheduleAlertsCol = styled.div`
  display: flex;
  flex: 1 0 auto;
`;

export const FullScheduleColumn = styled(FullScheduleAlertsCol)``;
export const AlertsColumn = styled(FullScheduleAlertsCol)`
  justify-content: flex-end;
`;

const FullSchedulAlertsButtonBase = styled(ClearButton)`
  display: inline-flex;
  flex: 0 0 auto;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  font-size: 1em;
  border-radius: ${props => props.theme.buttonRadius};
`;

export const ViewFullScheduleLink = styled(FullSchedulAlertsButtonBase).attrs({
  [GTM_DATA_ATTRIBUTE]: GTM_HANDLES.stop.fullScheduleLink,
})``;

export const AlertButton = styled(FullSchedulAlertsButtonBase).attrs({
  [GTM_DATA_ATTRIBUTE]: GTM_HANDLES.stop.stopDetailRouteAlertsButton,
})``;

export const AlertCount = styled.div`
  padding: 2px 6px 2px 8px;
  flex: 0 0 auto;
  font-weight: 500;
  margin-left: 5px;
`;

export const AlertsBadge = styled.div.attrs({
  role: 'presentation',
})`
  flex: 0 0 20px;
`;
